// src/pages/Dashboard/modules/TokenUsageByCompany.jsx
import React, { useEffect, useState } from 'react';
import { useNavigate } from "react-router-dom";
import { ChevronDown, Search } from 'lucide-react';
import { useDashboardFilters } from './context/DashboardContext';
import { useDispatch } from 'react-redux';
import { startLoader, stopLoader } from "../../_store/reducers/loaderReducer";
import { getCompanyWiseTokenDetails, getCompaniesList } from "../../requests.js";
import ResponseLoader from '../common/ResponseLoader.js';
import styles from "../../_css/ReportingAndSterring.module.css";

const TokenUsageByCompany = () => {
    const navigate = useNavigate();
    const [showDropdown, setShowDropdown] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedOption, setSelectedOption] = useState("Top 3");
    const [allCompanyData, setAllCompanyData] = useState([]);
    const [filteredCompanyData, setFilteredCompanyData] = useState([]);
    const { filters } = useDashboardFilters();
    const dispatch = useDispatch();
    const [loading, setLoading] = useState(false);
    const [companiesList, setCompaniesList] = useState([]);

    // Company options from getCompaniesList
    const [options, setOptions] = useState(["Top 3"]);

    // Check if global filter has a specific company selected (not 'all')
    const hasGlobalCompanyFilter = filters.company && filters.company !== 'all';

    const toggleDropdown = () => {
        if (!hasGlobalCompanyFilter) {
            setShowDropdown(!showDropdown);
        }
    };

    const filteredOptions = options.filter(opt =>
        opt.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const handleCompanyClick = (companyId, companyName) => {
        navigate(`/reporting/overviewDetails`, {
            state: {
                companyId,
                companyName
            }
        });
    };

    // Function to fetch companies list for dropdown
    const fetchCompaniesList = async () => {
        try {
            const companies = await getCompaniesList();
            if (companies && companies.result) {
                setCompaniesList(companies.result);
                // Generate options from companies list
                const companyNames = companies.result.map(company => company.name);
                setOptions(["Top 3", ...companyNames]);
            }
        } catch (error) {
            console.error("Error fetching companies list:", error);
        }
    };

    // Function to fetch company token usage data
    const fetchCompanyData = async () => {
        setLoading(true);
        dispatch(startLoader());
        try {
            const startDate = filters.dateRange?.from;
            const endDate = filters.dateRange?.to;
            const response = await getCompanyWiseTokenDetails({
                startDate,
                endDate
            });
            if (response?.data?.success) {
                const data = response.data?.data || [];
                setAllCompanyData(data);
                if (hasGlobalCompanyFilter) {
                    applyFilter(filters.company, data);
                } else {
                    applyFilter("Top 3", data);
                }
            }
        } catch (error) {
            console.error("Error fetching company data:", error);
        } finally {
            setLoading(false);
            dispatch(stopLoader());
        }
    };

    // Apply filter based on selected option
    const applyFilter = (option, data = allCompanyData) => {
        setSelectedOption(option);

        if (option === "Top 3") {
            // Show top 3 companies (sort by used tokens in descending order)
            const sortedData = [...data].sort((a, b) => b.usedToken - a.usedToken);
            setFilteredCompanyData(sortedData.slice(0, 3));
        } else {
            // Filter for specific company
            const filtered = data.filter(item =>
                item.company_name.toLowerCase() === option.toLowerCase()
            );
            setFilteredCompanyData(filtered);
        }
    };

    const handleOptionSelect = (option) => {
        if (!hasGlobalCompanyFilter) {
            setSearchTerm('');
            setShowDropdown(false);
            applyFilter(option);
        }
    };

    // Get display text for dropdown button
    const getDropdownButtonText = () => {
        if (hasGlobalCompanyFilter) {
            return filters.company; // Show the globally selected company
        }
        return selectedOption;
    };

    // Fetch companies list on component mount
    useEffect(() => {
        fetchCompaniesList();
    }, []);

    // Fetch company data when filters change
    useEffect(() => {
        fetchCompanyData();
    }, [filters]);

    // Update filtered data when global filter changes
    useEffect(() => {
        if (allCompanyData.length > 0) {
            if (hasGlobalCompanyFilter) {
                applyFilter(filters.company, allCompanyData);
            } else if (selectedOption === "Top 3") {
                applyFilter("Top 3", allCompanyData);
            }
        }
    }, [filters.company, allCompanyData]);


    // Format date function
    const formatDate = (dateString) => {
        if (!dateString) return '-';
        const options = { day: 'numeric', month: 'long', year: 'numeric' };
        return new Date(dateString).toLocaleDateString('en-US', options);
    };

    return (
        <div className={styles.tokenUsageContainer}>
            <div className="card-body">
                <div className={styles.tokenUsageHeader}>
                    <h5 className={`d-flex align-items-center ${styles.tokenUsageTitle}`}>
                        Token Usages By Company
                    </h5>
                    <div className={styles.dropdownWrapper}>
                        <button
                            onClick={toggleDropdown}
                            className={`${styles.dropdownButton} ${hasGlobalCompanyFilter ? styles.disabledDropdown : ''}`}
                            disabled={hasGlobalCompanyFilter}
                        >
                            {getDropdownButtonText()}
                            {!hasGlobalCompanyFilter && <ChevronDown size={16} color="#6b7280" />}
                        </button>

                        {showDropdown && !hasGlobalCompanyFilter && (
                            <div className={styles.dropdownMenu}>
                                {/* Search input */}
                                <div className={styles.searchWrapper}>
                                    <input
                                        type="text"
                                        placeholder="Search companies..."
                                        value={searchTerm}
                                        onChange={e => setSearchTerm(e.target.value)}
                                        className={styles.searchInput}
                                    />
                                    <Search
                                        size={16}
                                        color="#6B7280"
                                        className={styles.searchIcon}
                                    />
                                </div>

                                {/* Dropdown options */}
                                <div>
                                    {filteredOptions.map((option, idx) => (
                                        <div
                                            key={idx}
                                            onClick={() => handleOptionSelect(option)}
                                            className={`${styles.dropdownOption} ${option === selectedOption ? styles.activeOption : ''}`}
                                        >
                                            {option}
                                        </div>
                                    ))}
                                    {searchTerm && filteredOptions.length === 0 && (
                                        <div className={styles.noResults}>
                                            No companies found for "{searchTerm}"
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {loading ? (
                    <div className={styles.loaderContainer}>
                        <ResponseLoader />
                    </div>
                ) : (
                    <table className={`w-100 ${styles.tokenUsageTable}`}>
                        <thead className={styles.tableHead}>
                            <tr>
                                <th className={styles.companyHeader}>
                                    Company
                                </th>
                                {["Tokens Used", "Tokens Remaining", "Subscription Start", "Subscription End"].map((label, i) => (
                                    <th
                                        key={i}
                                        className={styles.tableHeader}
                                    >
                                        {label}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {filteredCompanyData.map((item, idx) => (
                                <tr key={idx} className={styles.tableRow}>
                                    <td
                                        onClick={() => handleCompanyClick(item.company_id, item.company_name)}
                                        className={styles.companyCell}
                                    >
                                        {item.company_name}
                                    </td>
                                    <td className={styles.tableCell}>{item.usedToken}</td>
                                    <td className={styles.tableCell}>{item.remainingToken}</td>
                                    <td className={styles.tableCell}>{formatDate(item.subscription_start)}</td>
                                    <td className={styles.tableCell}>{formatDate(item.subscription_end)}</td>
                                </tr>
                            ))}

                            {filteredCompanyData.length === 0 && (
                                <tr>
                                    <td colSpan="5" className={styles.noDataCell}>
                                        {hasGlobalCompanyFilter
                                            ? `No data available for company: ${filters.company}`
                                            : 'No company data available'
                                        }
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                )}
            </div>
        </div>
    );
};

export default TokenUsageByCompany;